import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { alreadyTranslatedText } from '@execonline-inc/translations';
import { identity } from '@kofno/piper';
import { Decoder, array, field, number, oneOf, string, succeed } from 'jsonous';
import { nothing } from 'maybeasy';
import { timeZoneDecoder } from '../../../../ProfileStore/Decoders';
import { assertionBackendResourceDecoder } from '../../../../Login/Decoders';
import { resourceDecoder } from '../../../../Resource/Decoders';
import { supportedLanguageCodeDecoder } from '../../../../SupportedLanguages/Decoders';
import { CommerceType, LicenseCommerceType, LoginMessageType } from '../../Common/Experience/Types';
import { organizationResourceDecoder } from '../../Decoders';
import {
  PreviewOnly,
  PreviewOnlyInvitationResource,
  SharedInvitationError,
  SharedInvitationErrorResource,
  SharedInvitationResource,
  UseCase,
  UseCaseResource,
  UseCasesProgramFamily,
  UseCasesProgramFamilyResource,
} from './Types';

export const commerceTypeDecoder: Decoder<CommerceType> = oneOf([
  stringLiteral<CommerceType>('cc-only'),
  stringLiteral<CommerceType>('cc-or-direct-bill'),
  stringLiteral<CommerceType>('not-available'),
]);

export const licenseCommerceTypeDecoder: Decoder<LicenseCommerceType> = oneOf([
  stringLiteral<LicenseCommerceType>('cc-only'),
  stringLiteral<LicenseCommerceType>('not-available'),
]);

export const loginMessageType: Decoder<LoginMessageType> = oneOf([
  stringLiteral<LoginMessageType>('use-case-description'),
  stringLiteral<LoginMessageType>('use-case-login-message'),
]);

export const useCaseDecoder: Decoder<UseCase> = succeed({})
  .assign('kind', field('kind', stringLiteral('use-case')))
  .assign('uuid', field('uuid', string))
  .assign('useCaseId', field('use_case_id', number))
  .assign('name', field('name', explicitMaybe(alreadyTranslatedText)))
  .assign('commerceType', field('commerce_type', commerceTypeDecoder))
  .assign('licenseCommerceType', field('license_commerce_type', licenseCommerceTypeDecoder))
  .assign('description', field('description', explicitMaybe(alreadyTranslatedText)))
  .assign('loginMessage', field('login_message', explicitMaybe(alreadyTranslatedText)))
  .assign('loginMessageType', field('login_message_type', loginMessageType))
  .assign('organization', field('organization', organizationResourceDecoder))
  .assign('assertionBackend', field('login', assertionBackendResourceDecoder))
  .assign('locale', field('locale', supportedLanguageCodeDecoder))
  .assign('timeZoneOptions', field('time_zone_options', explicitMaybe(array(timeZoneDecoder))))
  .map((decoded) => decoded as UseCase);

export const useCaseResourceDecoder: Decoder<UseCaseResource> = resourceDecoder(useCaseDecoder);

export const useCasesProgramFamilyDecoder: Decoder<UseCasesProgramFamily> = succeed({})
  .assign('kind', field('kind', stringLiteral('use-cases-program-family')))
  .assign('useCaseUuid', field('use_case_uuid', string))
  .assign('useCaseId', field('use_case_id', number))
  .assign('name', field('name', explicitMaybe(alreadyTranslatedText)))
  .assign('commerceType', field('commerce_type', commerceTypeDecoder))
  .assign('licenseCommerceType', field('license_commerce_type', licenseCommerceTypeDecoder))
  .assign('description', field('description', explicitMaybe(alreadyTranslatedText)))
  .assign('loginMessageType', field('login_message_type', loginMessageType))
  .assign('loginMessage', field('login_message', explicitMaybe(alreadyTranslatedText)))
  .assign('organization', field('organization', organizationResourceDecoder))
  .assign('programFamilyId', field('program_family_id', number))
  .assign('id', field('id', number))
  .assign('assertionBackend', field('login', assertionBackendResourceDecoder))
  .assign('locale', field('locale', supportedLanguageCodeDecoder))
  .assign('timeZoneOptions', field('time_zone_options', explicitMaybe(array(timeZoneDecoder))))
  .map((decoded) => decoded as UseCasesProgramFamily);

export const useCasesProgramFamilyResourceDecoder: Decoder<UseCasesProgramFamilyResource> =
  resourceDecoder(useCasesProgramFamilyDecoder);

export const previewOnlyDecoder: Decoder<PreviewOnly> = succeed({})
  .assign('kind', field('kind', stringLiteral('preview-only')))
  .assign('name', field('name', explicitMaybe(alreadyTranslatedText)))
  .assign('useCaseId', field('use_case_id', number))
  .assign('commerceType', field('commerce_type', commerceTypeDecoder))
  .assign('licenseCommerceType', succeed<LicenseCommerceType>('not-available'))
  .assign('description', field('description', explicitMaybe(alreadyTranslatedText)))
  .assign('organization', field('organization', organizationResourceDecoder))
  .assign('assertionBackend', field('login', assertionBackendResourceDecoder))
  .assign('loginMessageType', field('login_message_type', loginMessageType))
  .assign('loginMessage', field('login_message', explicitMaybe(alreadyTranslatedText)))
  .assign('locale', field('locale', supportedLanguageCodeDecoder))
  .assign('timeZoneOptions', succeed(nothing()))
  .map((decoded) => decoded as PreviewOnly);

export const previewOnlyInvitationResourceDecoder: Decoder<PreviewOnlyInvitationResource> =
  resourceDecoder(previewOnlyDecoder);

export const sharedInvitationResourceDecoder: Decoder<SharedInvitationResource> =
  oneOf<SharedInvitationResource>([
    useCaseResourceDecoder.map<SharedInvitationResource>(identity),
    useCasesProgramFamilyResourceDecoder.map<SharedInvitationResource>(identity),
    previewOnlyInvitationResourceDecoder.map<SharedInvitationResource>(identity),
  ]);

export const sharedInvitationErrorDecoder: Decoder<SharedInvitationError> = succeed({}).assign(
  'error',
  field('error', alreadyTranslatedText),
);

export const sharedInvitationErrorResourceDecoder: Decoder<SharedInvitationErrorResource> =
  resourceDecoder(sharedInvitationErrorDecoder);
